<template>
    <overlay ref="overlay"  @closed="$emit('closed')">
        <div class="main-container">
            <div class="header">
                Verwachte vertraging
            </div>
            <div class="body">
                <div v-if="notifying" data-uk-spinner="ratio: 4" />
                <div v-else-if="sendError" class="send-error">
                    Fout opgetreden bij het versturen van vertragings-notificatie(s) per SMS!
                </div>
                <div v-else-if="sendSuccess" class="send-success">
                    SMS notificatie(s) succesvol verzonden.
                </div>
                <template v-else>
                    <div class="hours">
                        <div>Uren</div>
                        <div class="time-input-wrapper" :class="{ 'updated': hoursUpdated }">
                            <button @click="editHour('add')" :disabled="hours >= 24" >+</button>
                            <input
                                v-model="hours"
                                type="text"
                                @input="handleHourInput()"
                            />
                            <button @click="editHour('minus')" :disabled="hours <= 0">-</button>
                        </div>
                    </div>
                    <div class="minutes">
                        <div>Minuten</div>
                        <div class="time-input-wrapper">
                            <button @click="editMinute('add')" :disabled="hours >= 24">+</button>
                            <input v-model="minutes" type="text" @input="handleMinuteInput()" />
                            <button @click="editMinute('minus')" :disabled="hours <= 0 && minutes <= 0">-</button>
                        </div>
                    </div>
                </template>
            </div>
            <div v-if="! sendError && ! sendSuccess" class="footer">
                <div v-if="notifying">
                    Bezig met informeren..
                </div>
                <div
                    v-else
                    class="send-button"
                    :class="{ 'disabled': ! validTimeSet }"
                    @click="sendSms()"
                >
                    Versturen
                </div>
            </div>
        </div>
    </overlay>
</template>
<style lang="scss">
    .time-input-wrapper {
        display:             flex;
        flex-direction:      column;
        transform:           scale(1);
        transition-duration: .2s;

        &.updated {
            transform: scale(1.1);
        }
        input {
            font-size:  30px;
            text-align: center;
            width:      60px;
            border:     none;
            outline:    none;
            padding:    5px 10px;
        }
        button {
            background: #ffffff;
            font-size:  30px;
            border:     none;
            outline:    none;
            padding:    10px 0;

            &:first-child {
                border-radius: 10px 10px 0 0;
            }
            &:last-child {
                border-radius: 0 0 10px 10px;
            }
        }
    }
    .main-container {
        background-color: var(--datepicker-background);
        border-radius:    10px;
        padding:          30px;
        margin-top:       100px;
        display:          flex;
        flex-direction:   column;
        align-items:      center;
        justify-content:  center;
        gap:              35px;

        .header,
        .body,
        .footer {
            width:           100%;
            display:         flex;
            flex-direction:  row;
            align-items:     center;
            justify-content: center;
        }
        .header {
            color:       var(--dynamic-title-color);
            font-weight: bold;
            font-size:   1.25em;
        }
        .body {
            gap: 15px;
            .hours,
            .minutes {
                display:         flex;
                flex-direction:  column;
                align-items:     center;
                justify-content: center;
                gap:             10px;
                color:           var(--dynamic-title-color);
            }
        }
        .footer {
            display:         flex;
            justify-content: center;

            .send-button {
                display:         flex;
                justify-content: center;
                align-items:     center;
                background:      #0e448d;
                color:           #fff;
                padding:         10px 20px;
                border-radius:   10px;
                cursor:          pointer;

                &.disabled {
                    background: #9d9d9d;
                    cursor:     not-allowed;
                }
            }
        }
        .send-error {
            color:       red;
            font-weight: 600;
        }
        .send-success {
            color:       #00a200;
            font-weight: 600;
        }
    }
</style>
<script>
import { apiClient } from "@/api";
import Overlay       from "@/components/overlay/Overlay.vue";

export default {
    name:       "DelayedPackingSlipDeliverySmsOverlay",
    components: { Overlay },
    props: {
        ride: { type: Object, required: true },
    },
    data() {
        return {
            hours:        0,
            hoursUpdated: false,
            minutes:      0,
            notifying:    false,
            sendError:    false,
            sendSuccess:  false,
        }
    },
    computed: {
        validTimeSet() {
            return this.hours + this.minutes > 0;
        },
    },
    methods: {
        show() {
            this.$refs.overlay.show();
        },
        hide() {
            this.$refs.overlay.hide();
        },
        editHour(type) {
            if (type === 'add' && this.hours < 24) {
                this.hours++;
            }
            if (type === 'minus' && this.hours > 0) {
                this.hours--;
            }
        },
        editMinute(type) {
            if (type === 'add') {
                const step = Math.abs(((this.minutes + 15) % 15) - 15) || 15;

                if (this.minutes + step < 60) {
                    this.minutes += step;
                } else {
                    this.minutes += step;
                    this.minutes -= 60;
                    this.editHour('add');
                    this.hoursUpdated = true;
                    setTimeout(() => { this.hoursUpdated = false }, 100);
                }
            }
            if (type === 'minus') {
                const step = this.minutes % 15 || 15;
                if (this.minutes - step >= 0) {
                    this.minutes -= step;
                } else {
                    this.minutes -= step;
                    this.minutes += 60;
                    this.editHour('minus');
                    this.hoursUpdated = true;
                    setTimeout(() => { this.hoursUpdated = false }, 100);
                }
            }
        },
        handleHourInput() {
            if (isNaN(this.hours) || this.hours === "") this.hours = 0;
            this.hours = parseInt(this.hours);

            if (this.hours > 24) this.hours = 24;
            if (this.hours < 0)  this.hours = 0;
        },
        handleMinuteInput() {
            if (isNaN(this.minutes) || this.minutes === "") this.minutes = 0;
            this.minutes = parseInt(this.minutes);

            if (this.minutes > 59) this.minutes = 59;
            if (this.minutes < 0)  this.minutes = 0;
        },
        async sendSms() {
            if (! this.validTimeSet) {
                return;
            }
            try {
                this.notifying = true;
                await apiClient.post(
                    `transport/transport_rides/${this.ride.id}/delayed_delivery_sms`,
                    {
                        delayedHours:   this.hours,
                        delayedMinutes: this.minutes
                    }
                )
                this.sendSuccess = true;
            } catch (e) {
                this.sendError = true;
                console.error(e); //eslint-disable-line no-console
            } finally {
                this.notifying = false;
            }
        },
    }
}
</script>
